import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Search, X, Plus } from "lucide-react";
import {
  fetchProgress, fetchKanjiList, fetchUnlockedWords, fetchUnlockedKanji,
  fetchWordProgress, setWordProgress, setProgress,
} from "@/lib/api";
import JlptBadge from "@/components/JlptBadge";

const STATUS_COLOR = {
  learning:      "#D29C4C", // amber
  "ready-kanji": "#6E7A5A", // moss
  "ready-word":  "#3E7C74", // teal
  known:         "#4A6D7C", // indigo
  new:           "#7A746D", // muted
};
const STATUS_LABEL = {
  learning:      "Learning",
  "ready-kanji": "Ready kanji",
  "ready-word":  "Ready compounds",
  known:         "Known",
  new:           "New",
};
const STATUS_ORDER = ["learning", "ready-kanji", "ready-word", "known", "new"];
const JLPT_LEVELS = ["ALL", "N5", "N4", "N3", "N2", "N1"];

function FilterChip({ active, onClick, children, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`px-3 py-1 text-[10px] uppercase tracking-[0.25em] font-sans border transition-colors ${
        active ? "bg-sumi text-washi border-sumi" : "text-sumi border-borderDefault hover:border-sumi"
      }`}
    >
      {children}
    </button>
  );
}

/* A single tile that can render either a kanji or a compound. */
function Tile({ item, onClick, onAdd }) {
  const isCompound = item.type === "word";
  const ring = STATUS_COLOR[item.status];
  return (
    <div
      data-testid={`journal-item-${item.key}`}
      className="bg-washi border hover:border-shu transition-colors flex items-center"
      style={{ borderColor: ring, borderLeftColor: ring, borderLeftWidth: 3 }}
    >
      <button
        onClick={onClick}
        className="flex-1 text-left px-3 py-2 min-w-0"
      >
        <div className="flex items-baseline gap-2 flex-wrap">
          <span className={`font-serif ${isCompound ? "text-xl" : "text-3xl"} text-sumi leading-none`}>
            {item.label}
          </span>
          {item.reading && (
            <span className="font-serif text-xs text-sumiSoft">{item.reading}</span>
          )}
          <JlptBadge level={item.jlpt} jouyou={item.jouyou} size="sm" className="ml-auto" />
        </div>
        <p className="font-sans text-[11px] text-sumi mt-1 leading-snug truncate">
          {item.meaning}
        </p>
      </button>
      {onAdd && (
        <button
          data-testid={`journal-add-${item.key}`}
          onClick={(e) => { e.stopPropagation(); onAdd(); }}
          title="Add to learning deck"
          aria-label={`Add ${item.label}`}
          className="shrink-0 p-2 border-l border-borderDefault hover:bg-shu hover:text-washi transition-colors text-sumi"
        >
          <Plus size={14} />
        </button>
      )}
    </div>
  );
}

export default function Journal({ onSelectKanji }) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const progQ = useQuery({ queryKey: ["progress"], queryFn: fetchProgress });
  const allQ = useQuery({
    queryKey: ["all-kanji"],
    queryFn: () => fetchKanjiList({ level: "ALL", limit: 2200 }),
  });
  const unlockedQ = useQuery({
    queryKey: ["unlocked-words"],
    queryFn: () => fetchUnlockedWords(150),
  });
  const unlockedKanjiQ = useQuery({
    queryKey: ["unlocked-kanji"],
    queryFn: () => fetchUnlockedKanji(120),
  });
  const wordProgQ = useQuery({ queryKey: ["word-progress"], queryFn: fetchWordProgress });
  const addLearning = useMutation({
    mutationFn: (word) => setWordProgress(word, "learning"),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["word-progress"] }),
  });
  const addLearningKanji = useMutation({
    mutationFn: (ch) => setProgress(ch, "learning"),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["progress"] });
      qc.invalidateQueries({ queryKey: ["unlocked-kanji"] });
    },
  });

  const [jlptFilter, setJlptFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [query, setQuery] = useState("");

  const ql = query.trim().toLowerCase();
  const isLoading = progQ.isLoading || allQ.isLoading;

  /* Build a single flat list of tiles, each with a status. */
  const tiles = useMemo(() => {
    const allKanji = {};
    (allQ.data?.items || []).forEach((k) => { allKanji[k.kanji] = k; });
    const wordProg = wordProgQ.data?.items || {};
    const list = [];

    // kanji from /api/progress
    Object.entries(progQ.data?.items || {}).forEach(([ch, p]) => {
      const k = allKanji[ch] || { kanji: ch, meaning: "", jlpt: null };
      const clean = (r) => r.replace(/^-|-$/g, "").replace(/\./g, "");
      const on = (k.on_readings || []).map(clean).filter(Boolean).slice(0, 2);
      const kun = (k.kun_readings || []).map(clean).filter(Boolean).slice(0, 2);
      const readingParts = [];
      if (on.length) readingParts.push(on.join("・"));
      if (kun.length) readingParts.push(kun.join("・"));
      list.push({
        key: `k-${ch}`,
        type: "kanji",
        char: ch,
        label: ch,
        meaning: k.meaning || "",
        reading: readingParts.join("  ") || null,
        jlpt: k.jlpt,
        jouyou: k.jouyou,
        status: p?.status || "new",
      });
    });

    // compounds: tracked OR ready (untracked but fully unlocked)
    const seenWord = new Set();
    (unlockedQ.data?.items || []).forEach((v) => {
      if (seenWord.has(v.word)) return;
      seenWord.add(v.word);
      const userStatus = wordProg[v.word]?.status;
      list.push({
        key: `w-${v.word}`,
        type: "word",
        word: v.word,
        label: v.word,
        meaning: v.meaning,
        reading: v.reading,
        jlpt: v.jlpt,
        status: userStatus || "ready-word",
      });
    });
    // also any compound the user has tracked but isn't in unlocked list
    Object.entries(wordProg).forEach(([w, p]) => {
      if (seenWord.has(w)) return;
      seenWord.add(w);
      list.push({
        key: `w-${w}`,
        type: "word",
        word: w,
        label: w,
        meaning: "",
        reading: "",
        jlpt: null,
        status: p?.status || "new",
      });
    });

    // ready-to-learn KANJI — single kanji whose every component is known.
    // Don't overwrite kanji that are already tracked (learning/known/new).
    const seenKanji = new Set(list.filter((t) => t.type === "kanji").map((t) => t.char));
    (unlockedKanjiQ.data?.items || []).forEach((k) => {
      if (seenKanji.has(k.kanji)) return;
      seenKanji.add(k.kanji);
      list.push({
        key: `k-${k.kanji}`,
        type: "kanji",
        char: k.kanji,
        label: k.kanji,
        meaning: k.meaning || "",
        reading: (k.components || []).join(" + ") || null,
        jlpt: k.jlpt,
        status: "ready-kanji",
      });
    });
    return list;
  }, [progQ.data, allQ.data, unlockedQ.data, unlockedKanjiQ.data, wordProgQ.data]);

  /* Apply filters. */
  const visible = useMemo(() => tiles.filter((t) => {
    if (jlptFilter !== "ALL" && t.jlpt !== jlptFilter) return false;
    if (statusFilter !== "ALL" && t.status !== statusFilter) return false;
    if (!ql) return true;
    if (t.label.includes(query)) return true;
    if ((t.meaning || "").toLowerCase().includes(ql)) return true;
    if (t.reading && t.reading.includes(query)) return true;
    return false;
  }), [tiles, jlptFilter, statusFilter, ql, query]);

  /* Group by status — fixed display order. */
  const groups = useMemo(() => {
    const out = {};
    STATUS_ORDER.forEach((s) => { out[s] = []; });
    visible.forEach((t) => {
      if (out[t.status]) out[t.status].push(t);
    });
    return out;
  }, [visible]);

  /* Counts per status (unfiltered) for the header. */
  const totals = useMemo(() => {
    const c = { learning: 0, "ready-kanji": 0, "ready-word": 0, known: 0, new: 0 };
    tiles.forEach((t) => { c[t.status] = (c[t.status] || 0) + 1; });
    return c;
  }, [tiles]);

  const handleClick = (t) => {
    if (t.type === "word") navigate(`/word/${encodeURIComponent(t.word)}`);
    else onSelectKanji?.(t.char);
  };

  const hasAnyFilter = jlptFilter !== "ALL" || statusFilter !== "ALL" || query !== "";

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-8">
      <h2 className="font-serif text-3xl mb-2">Your Journal</h2>
      <p className="text-sumiSoft text-sm font-sans mb-6">
        {isLoading
          ? "loading…"
          : tiles.length === 0
            ? "Nothing here yet — pick a kanji from the Build path and mark it Learning or Known."
            : `${totals.learning} learning · ${totals["ready-kanji"]} ready kanji · ${totals["ready-word"]} ready compounds · ${totals.known} known · ${totals.new} new`}
      </p>

      {tiles.length > 0 && (
        <div className="mb-7 border border-borderDefault bg-washiPanel/50 px-3 py-3 flex flex-col gap-3" data-testid="journal-filters">
          <div className="flex items-center gap-2">
            <Search size={14} className="text-sumiSoft shrink-0" />
            <input
              data-testid="journal-search"
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="search by kanji, reading, or meaning…"
              lang="ja"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              className="flex-1 bg-transparent text-sm font-sans text-sumi placeholder:text-sumiSoft focus:outline-none"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-sumiSoft hover:text-sumi" data-testid="journal-search-clear" aria-label="clear">
                <X size={14} />
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft self-center mr-1">jlpt</span>
            {JLPT_LEVELS.map((lvl) => (
              <FilterChip key={lvl} active={jlptFilter === lvl} onClick={() => setJlptFilter(lvl)} testId={`journal-jlpt-${lvl}`}>{lvl}</FilterChip>
            ))}
          </div>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft self-center mr-1">status</span>
            {["ALL", ...STATUS_ORDER].map((s) => (
              <FilterChip key={s} active={statusFilter === s} onClick={() => setStatusFilter(s)} testId={`journal-status-${s}`}>
                {s === "ALL" ? "All" : STATUS_LABEL[s]}
              </FilterChip>
            ))}
            {hasAnyFilter && (
              <button
                onClick={() => { setJlptFilter("ALL"); setStatusFilter("ALL"); setQuery(""); }}
                data-testid="journal-clear-filters"
                className="ml-auto text-[10px] uppercase tracking-[0.25em] font-sans text-sumiSoft hover:text-shu px-2 py-1"
              >
                reset
              </button>
            )}
          </div>
        </div>
      )}

      {STATUS_ORDER.map((status) => {
        const list = groups[status];
        if (!list.length) return null;
        return (
          <motion.section
            key={status}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-10"
            data-testid={`journal-section-${status}`}
          >
            <div className="flex items-baseline gap-3 mb-3">
              <span className="font-serif text-2xl" style={{ color: STATUS_COLOR[status] }}>
                {STATUS_LABEL[status]}
              </span>
              <span className="text-[10px] uppercase tracking-[0.25em] text-sumiSoft font-sans">
                {list.length}
                {list.length !== totals[status] && (
                  <span className="opacity-60"> / {totals[status]}</span>
                )}
              </span>
              <span className="hairline h-px flex-1 mt-3 bg-borderDefault" />
            </div>
            {status === "ready-kanji" && (
              <p className="text-xs text-sumiSoft font-sans mb-3 italic">
                New kanji built entirely from parts you already know — tap <Plus size={11} className="inline" /> to add one to your learning deck.
              </p>
            )}
            {status === "ready-word" && (
              <p className="text-xs text-sumiSoft font-sans mb-3 italic">
                Compound words where every kanji is in your Known set — tap <Plus size={11} className="inline" /> to add one to your learning deck.
              </p>
            )}
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              {list.map((t) => (
                <Tile
                  key={t.key}
                  item={t}
                  onClick={() => handleClick(t)}
                  onAdd={
                    status.startsWith("ready")
                      ? t.type === "word"
                        ? () => addLearning.mutate(t.word)
                        : () => addLearningKanji.mutate(t.char)
                      : null
                  }
                />
              ))}
            </div>
          </motion.section>
        );
      })}

      {hasAnyFilter && visible.length === 0 && (
        <p className="text-sumiSoft text-sm font-sans italic mt-8 text-center">
          No entries match your filters.
        </p>
      )}
    </div>
  );
}
